/* oxlint-disable react/no-danger -- this file exists for the inline script below */
import { EGRESS_BOOTED_FLAG, EGRESS_ROUTE_PREFIX } from 'src/utils/egress';

// Tells the stage endpoint that the egress document arrived and parsed, and
// recovers a page whose scripts never run.
//
// The `boot` stage in `lk-recording/*/layout.tsx` runs from a module body, so it
// needs the layout chunk to download and run first. A recording that fails with
// "Start signal not received" usually sends no stage at all. Two causes then look
// the same: the document never arrived, or it arrived and the hydration scripts
// failed.
//
// An effect cannot tell them apart, because a component that does not hydrate
// cannot report that it did not hydrate. This script runs from the server-rendered
// `<head>` instead. Next gives every chunk tag the `async` attribute, so no chunk
// stops the parser and the script reports even if no chunk runs.
//
// The watchdog: if the layout does not set `EGRESS_BOOTED_FLAG`, the script sends
// a stall report and reloads the page one time. It does this at the first
// `/_next/static/` script that fails, because the layout cannot run without all
// of its startup chunks, and each second of wait is a second the recording loses.
// A hung chunk request sends no error event, so a timer catches that case. The
// watchdog cannot reload during a recording, because recording starts only after
// the layout ran. LiveKit keeps a page that never sends its start signal open
// until the egress is stopped, so a reload has time to work.
//
// When the page cannot reload, the failed script does not send the report. The
// timer sends it, because by then the counts are complete.
//
// The stage stays `boot` because a zod schema in another repo validates
// `EgressStage`. `meta.preHydration` separates the two, so a count of `boot` alone
// now includes both and a query must select one.
//
// Keep the body in agreement with `recordEgressStage` in `lk-recording/lib/stage.ts`.

// Healthy pages set the flag in less than 8 seconds.
export const WATCHDOG_MS = 15_000;

// Holds the once-only reload flag. A reload keeps `sessionStorage`, and the URL
// must not change because `EgressHelper` parses it.
export const RELOAD_KEY = 'askable-egress-watchdog-reloaded';

// Makes a value safe inside an inline `<script>`. A `</script` in a value would
// close the tag too soon.
const forInlineScript = (value: string) => JSON.stringify(value).replaceAll('<', '\\u003c');

export const buildBeaconScript = (): string | null => {
  const graphqlUrl = process.env.NEXT_PUBLIC_GRAPHQL_URL;
  if (!graphqlUrl) {
    return null;
  }

  const endpoint = forInlineScript(`${graphqlUrl}/lk-recording-stage`);
  const prefix = forInlineScript(EGRESS_ROUTE_PREFIX);
  const bootedFlag = forInlineScript(EGRESS_BOOTED_FLAG);
  const reloadKey = forInlineScript(RELOAD_KEY);

  // Each step has a try block. A throw here loses the recording that the script
  // must explain. The stall report skips `nomodule` scripts, because a modern
  // browser never fetches them and they would always look pending.
  //
  // `failed` holds each URL one time. A chunk has a `<script>` tag and a preload
  // `<link>`, and both send an error event when the request fails.
  //
  // The `firstFailed*` fields come from the resource timing entry of the first
  // failed URL. An HTTP error has its status. Chrome gives a network error the
  // status 0. The fields are null when the browser has no entry.
  return `(function(){try{
if(window.location.pathname.indexOf(${prefix})!==0)return;
var params=new URLSearchParams(window.location.search);
var egressId=null,roomName=null;
try{
var token=params.get('token');
var part=token?token.split('.')[1]:null;
if(part){
var claims=JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/')));
var identity=claims.identity||claims.sub;
if(typeof identity==='string'&&identity.indexOf('EG_')===0)egressId=identity;
roomName=(claims.video&&claims.video.room)||null;
}
}catch(e){}
var send=function(meta){try{
fetch(${endpoint},{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({stage:'boot',at:new Date().toISOString(),egressId:egressId,layout:params.get('layout'),roomName:roomName,error:null,meta:meta}),keepalive:true,mode:'cors',credentials:'omit'}).catch(function(){});
}catch(e){}};
var reloaded=false,canReload=false;
try{reloaded=window.sessionStorage.getItem(${reloadKey})==='1';canReload=!reloaded;}catch(e){}
send({preHydration:true,reloaded:reloaded});
var STATIC='/_next/static/';
var failed=[],firstError=null,stalled=false;
var path=function(url){return url?String(url).replace(/^[a-z]+:\\/\\/[^/]+/,'').slice(0,200):null;};
var stall=function(trigger){try{
if(stalled||window[${bootedFlag}])return;
stalled=true;
var done={},entries=window.performance.getEntriesByType('resource');
for(var i=0;i<entries.length;i++)done[entries[i].name]=true;
var scripts=window.document.querySelectorAll('script[src*="'+STATIC+'"]:not([nomodule])');
var loaded=0,pending=[];
for(var j=0;j<scripts.length;j++){var src=scripts[j].src;if(failed.indexOf(src)!==-1)continue;if(done[src])loaded++;else pending.push(src);}
var entry=null;
try{var named=failed.length?window.performance.getEntriesByName(failed[0]):[];entry=named.length?named[named.length-1]:null;}catch(x){}
var num=function(value){return typeof value==='number'?Math.round(value):null;};
send({preHydration:true,reloaded:reloaded,watchdog:true,trigger:trigger,readyState:window.document.readyState,scripts:scripts.length,loaded:loaded,failed:failed.length,pending:pending.length,firstFailed:path(failed[0]),firstFailedStatus:entry?num(entry.responseStatus):null,firstFailedMs:entry?num(entry.duration):null,firstFailedBytes:entry?num(entry.transferSize):null,firstPending:path(pending[0]),firstError:firstError});
if(!canReload)return;
try{window.sessionStorage.setItem(${reloadKey},'1');}catch(x){return;}
window.location.reload();
}catch(e){}};
window.addEventListener('error',function(e){try{
var t=e.target,url=t&&(t.src||t.href);
if(t&&t!==window&&typeof url==='string'){
if(url.indexOf(STATIC)===-1)return;
if(failed.indexOf(url)===-1)failed.push(url);
if(canReload&&t.tagName==='SCRIPT')stall('script-error');
return;}
if(firstError===null)firstError=String(e.message||'').slice(0,200);
}catch(x){}},true);
setTimeout(function(){stall('timer');},${WATCHDOG_MS});
}catch(e){}})();`;
};

// Built one time. The build inlines the endpoint from the environment.
const script = buildBeaconScript();

export const EgressPrebootBeacon = () => {
  if (!script) {
    return null;
  }

  return <script dangerouslySetInnerHTML={{ __html: script }} />;
};
